import { useEventCallback, useUpdateEffect } from '@cian/react-utils';

import * as R from 'ramda';
import { type FC, useEffect, useRef } from 'react';

import { type IJsonQueryPolygon } from '@/packages/api-models/common/json_query';

import { useMapStoreContext } from '../../context/mapStoreContext';
import { type IPolygon } from '../../types/Map';

import { DEFAULT_OPTIONS } from './constants';
import { trackAreaSelect } from './tracking';

export interface IPolygonsProps {
  polygons: IPolygon[];
  isDrawingMode: boolean;
  onAddPolygon(polygon: IJsonQueryPolygon): void;
}

export const MapPolygons: FC<IPolygonsProps> = ({ polygons, isDrawingMode, onAddPolygon }) => {
  const { ymaps, map } = useMapStoreContext();
  const drawPolygonBehaviorRef = useRef<YMaps.DrawPolygon | null>(null);
  const geoObjectsRef = useRef<{ [id: string]: null | YMaps.IGeoObject }>({});

  const removeGeoObjects = useEventCallback((ids: string[]) => {
    ids.forEach((id: string) => {
      const geoObject = geoObjectsRef.current[id];

      if (geoObject) {
        map.geoObjects.remove(geoObject);
      }
    });
  });

  const updateGeoObjects = useEventCallback((currentPolygons: IPolygon[]) => {
    const aliveKeys = Object.keys(geoObjectsRef.current);
    const actualKeys = currentPolygons.map(({ id }) => id);
    const toRemove = R.difference(aliveKeys, actualKeys);

    removeGeoObjects(toRemove);

    currentPolygons.forEach((polygon: IPolygon) => {
      const { id } = polygon;

      if (!geoObjectsRef.current[id]) {
        const geoObject = new ymaps.Polygon([polygon.coordinates], {}, DEFAULT_OPTIONS) as unknown as YMaps.IGeoObject;

        map.geoObjects.add(geoObject);

        geoObjectsRef.current[id] = geoObject;
      }
    });
  });

  const handleAddPolygon = useEventCallback((coordinates: [number, number][]): void => {
    onAddPolygon({
      type: 'polygon',
      name: 'Выделенная область',
      coordinates: coordinates.map(item => item.map(String) as [string, string]),
    });

    drawPolygonBehaviorRef.current?.disable();
    trackAreaSelect('success');
  });

  useEffect(() => {
    if (!ymaps || !map || drawPolygonBehaviorRef.current) {
      return (): void => {};
    }

    drawPolygonBehaviorRef.current = new ymaps.cian.DrawPolygon({
      map,
      onCreatePolygon: handleAddPolygon,
    });

    updateGeoObjects(polygons);

    return (): void => {
      drawPolygonBehaviorRef.current?.destroy();

      removeGeoObjects(Object.keys(geoObjectsRef.current));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(() => {
    if (isDrawingMode) {
      drawPolygonBehaviorRef.current?.enable();
    } else {
      drawPolygonBehaviorRef.current?.disable();
    }
  }, [isDrawingMode]);

  useUpdateEffect(() => {
    updateGeoObjects(polygons);
  }, [polygons]);

  return null;
};
